import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Star, ShieldCheck, Quote } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" }); } catch { return ""; }
}

export default function FeaturedReviews() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/reviews/featured?limit=8`)
      .then((r) => setReviews(r.data || []))
      .catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (reviews.length === 0) return null;

  const r = reviews[idx];

  return (
    <section className="bg-[#2C2A29] text-white py-20" data-testid="featured-reviews">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="label-small text-[#E8A99E] mb-3">Notes from our patrons</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">Love, in every rating</h2>
        </div>

        <div className="relative">
          <Quote className="absolute -top-4 left-1/2 -translate-x-1/2 h-10 w-10 text-[#C5A059]/40" />

          <div key={r.review_id} className="fade-in text-center max-w-3xl mx-auto" data-testid={`featured-review-${r.review_id}`}>
            <div className="flex items-center justify-center gap-1 mb-6">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-5 w-5 ${n <= (r.rating || 0) ? "fill-[#C5A059] text-[#C5A059]" : "text-white/30"}`} />
              ))}
            </div>

            {r.title && (
              <h3 className="font-display text-2xl sm:text-3xl mb-4 text-white">{r.title}</h3>
            )}
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-6 font-light italic whitespace-pre-line line-clamp-6">
              &ldquo;{r.body}&rdquo;
            </p>

            {(r.images || []).length > 0 && (
              <div className="flex justify-center gap-2 mb-6">
                {r.images.slice(0, 3).map((src, i) => (
                  <div key={i} className="h-16 w-16 rounded-xl overflow-hidden border border-white/10">
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-center gap-2 flex-wrap text-sm text-white/70">
              <span className="text-white font-semibold">{r.reviewer_name || "A Sumanya patron"}</span>
              {r.is_verified_buyer && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                  <ShieldCheck className="h-3 w-3" /> Verified purchase
                </span>
              )}
              <span className="text-white/40">·</span>
              <Link
                to={`/product/${r.product_id}`}
                className="text-[#E8A99E] hover:text-[#C5A059] transition-colors"
                data-testid={`featured-review-product-${r.review_id}`}
              >
                {r.product_name}
              </Link>
              <span className="text-white/40">·</span>
              <span>{fmtDate(r.approved_at || r.created_at)}</span>
            </div>
          </div>

          {reviews.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10" data-testid="featured-review-dots">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-[#C5A059]" : "w-2 bg-white/25 hover:bg-white/40"}`}
                  aria-label={`Show review ${i + 1}`}
                  data-testid={`featured-review-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
